import { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { Pages } from '../../components/Pages/Pages';
import WithAuthentication from '../../components/WithAuthentication/WithAuthentication';
import { ApplicationState } from '../../domain/types';
import { useAppDispatch } from '../../hooks';
import useApplicationStateRefresh from '../../hooks/useApplicationStateRefresh';
import useGetApplicationSummary from '../../hooks/useGetApplicationSummaryService';
import useSettings from '../../hooks/useSettings';
import CoApplicantConsolidationPage from '../../pages/ConsolidationPage/CoApplicantConsolidationPageNO';
import CoApplicantEmploymentPage from '../../pages/EmploymentPage/CoApplicantEmploymentPageNO';
import ErrorPage from '../../pages/ErrorPage/ErrorPage';
import CoApplicantExpensePage from '../../pages/ExpensePage/CoApplicantExpensePage';
import CoApplicantHowToApplyPage from '../../pages/HowToApplyPage/CoApplicantHowToApplyPage';
import CoApplicantIncomePage from '../../pages/IncomePage/CoApplicantIncomePage';
import CoApplicantKycOngoingPage from '../../pages/KycPage/CoApplicantKycOngoingPage';
import CoApplicantKycPage from '../../pages/KycPage/CoApplicantKycPage';
import CoApplicantLivingConditionPage from '../../pages/LivingConditionPage/CoApplicantLivingConditionPage';
import CoApplicantLogInPage from '../../pages/LogInPage/CoApplicantLogInPage';
import CoApplicantSignAgreementPage from '../../pages/SignAgreementPage/CoApplicantSignAgreementPage';
import OngoingStaticPage from '../../pages/StatusPage/OngoingStaticPage';
import CoApplicantSubmitPage from '../../pages/SubmitPage/CoApplicantSubmitPage';
import { selectAccessToken } from '../../store/authentication';
import { setCurrentPage } from '../../store/wizard/wizard';

const SubmitCoApplicantWizard = () => {
    const accessToken = useSelector(selectAccessToken);
    const { isError, applicationState } = useApplicationStateRefresh();
    const dispatch = useAppDispatch();
    const settings = useSettings();
    const summary = useGetApplicationSummary();
    // `undefined` while the summary is loading; the page waits before deciding.
    const isCoApplicantConsolidation =
        summary === undefined ? undefined : Boolean(summary.isConsolidation && settings.withCoApplicantConsolidations);

    useEffect(() => {
        if (applicationState === 'KYC_CO_APPLICANT' || applicationState === 'SIGN_AGREEMENT') {
            dispatch(setCurrentPage(0));
        }
    }, [dispatch, applicationState]);

    if (isError) {
        return <ErrorPage />;
    }

    const getPage = () => {
        if (!accessToken) {
            return (
                <Pages>
                    <CoApplicantHowToApplyPage />
                    <CoApplicantLogInPage />
                </Pages>
            );
        } else {
            switch (applicationState) {
                case 'CO_SUBMIT':
                    return (
                        <WithAuthentication>
                            <Pages>
                                <CoApplicantHowToApplyPage />
                                <CoApplicantLogInPage />
                                <CoApplicantConsolidationPage isConsolidation={isCoApplicantConsolidation} />
                                <CoApplicantLivingConditionPage />
                                <CoApplicantEmploymentPage />
                                <CoApplicantIncomePage />
                                <CoApplicantExpensePage />
                                <CoApplicantKycPage />
                                <CoApplicantSubmitPage />
                            </Pages>
                        </WithAuthentication>
                    );

                case 'KYC_CO_APPLICANT':
                    return (
                        <WithAuthentication>
                            <Pages>
                                <CoApplicantKycOngoingPage />
                            </Pages>
                        </WithAuthentication>
                    );

                case 'SIGN_AGREEMENT':
                    return (
                        <WithAuthentication>
                            <Pages>
                                <CoApplicantSignAgreementPage />
                            </Pages>
                        </WithAuthentication>
                    );

                default:
                    return (
                        <WithAuthentication>
                            <OngoingStaticPage applicationState={applicationState as ApplicationState} />
                        </WithAuthentication>
                    );
            }
        }
    };

    return <>{getPage()}</>;
};

export default SubmitCoApplicantWizard;
